/**
 * Pure predicates for the two "no entity can be resolved" states.
 *
 * Kept in its own module with no React and no query-client import so it is
 * directly unit-testable; `entity-context.tsx` cannot be imported from an L1
 * test (this repo has no jsdom environment at that layer).
 *
 * @spec DOMAIN D23
 */

/** The entity fields these predicates read. A subset of `Entity`. */
export interface ScopeCandidateEntity {
  isArchived?: boolean | null;
}

/**
 * True when the entities list resolved fine but holds nothing to scope to -
 * an empty workspace, or one whose every entity is archived so
 * `pickDefaultLandingEntity` can never land on one.
 *
 * Deliberately false while loading (nothing is known yet) and false when the
 * load failed (that is `entitiesLoadFailed`, a different state with a
 * different remedy). Conflating the two is what made the failure silent:
 * "nothing to show" and "we could not find out" read identically to a user
 * once every list renders empty, but only one of them is recoverable by
 * retrying.
 */
export function hasNoSelectableEntity(
  entities: readonly ScopeCandidateEntity[],
  state: { isLoading: boolean; loadFailed: boolean; },
): boolean {
  if (state.isLoading || state.loadFailed) return false;
  return entities.every((entity) => !!entity.isArchived);
}

/**
 * The three states in which the server cannot answer a per-entity request,
 * and the affordance each one deserves.
 *
 * `rollup` and `archived` were always covered. `absent` was not: no concrete
 * entity is resolved at all, either because nothing was ever selected (a
 * workspace whose every entity is archived, so `pickDefaultLandingEntity`
 * never lands) or because the persisted id no longer names an entity in this
 * workspace. The header is then unresolvable server-side, so a concrete read
 * answers not-found and a write refuses with E89 - the same outcomes the other
 * two reasons already block on. `config-tabs.tsx` had been open-coding
 * `blocked || !activeEntityId` to compensate; this makes it the guard's job.
 *
 * @spec ILLEGAL E89
 * @spec ILLEGAL E90
 */
export type EntityScopeBlockReason = "rollup" | "archived" | "absent";

export type EntityScopeBlock =
  | { blocked: false; reason: null; }
  | { blocked: true; reason: EntityScopeBlockReason; };

export function resolveEntityScopeBlock(state: {
  isAllEntities: boolean;
  isActiveEntityArchived: boolean;
  /** A concrete entity from the loaded list, not just a persisted id string. */
  hasActiveEntity: boolean;
  isLoading: boolean;
}): EntityScopeBlock {
  if (state.isAllEntities) return { blocked: true, reason: "rollup" };
  if (state.isActiveEntityArchived) return { blocked: true, reason: "archived" };
  // Deliberately never blocked while loading: `entities` is empty then, so
  // every screen would flash "pick an entity" on boot before resolving one.
  if (!state.isLoading && !state.hasActiveEntity) return { blocked: true, reason: "absent" };
  return { blocked: false, reason: null };
}

/**
 * What a pending "retry loading entities" should do next.
 *
 * The invalidation cannot ride on the refetch promise. `getActiveEntityHeader`
 * reads the entity out of localStorage, and localStorage is only written by
 * the provider's selection effect - on a render *after* the refetch resolves.
 * Invalidating inside the promise callback therefore refetches every list
 * before any header exists, and `staleTime: Infinity` then pins that second
 * empty answer exactly as it pinned the first. Waiting for the scope to
 * resolve is the whole point of the retry.
 *
 * - `wait` - nothing to do yet (no retry pending, or still resolving).
 * - `invalidate` - a scope is resolved, so drop the empty caches now.
 * - `settle` - the retry finished but there is still no scope to fetch under,
 *   so there is nothing worth dropping; clear the flag and leave
 *   `EntityScopeBanner` to explain.
 */
export function resolveRetryInvalidation(state: {
  pending: boolean;
  isLoading: boolean;
  /** A concrete entity resolved, or the explicit rollup - either sends a header. */
  hasResolvedScope: boolean;
  noSelectableEntity: boolean;
  loadFailed: boolean;
}): "wait" | "invalidate" | "settle" {
  if (!state.pending || state.isLoading) return "wait";
  if (state.hasResolvedScope) return "invalidate";
  if (state.noSelectableEntity || state.loadFailed) return "settle";
  return "wait";
}

/**
 * The i18n key for the one-line hint on a disabled action.
 *
 * `EntityScopeRequired` replaces a whole screen. This is for the smaller case
 * where one button is disabled and the reason is otherwise invisible: a
 * disabled control with no explanation is a dead end, and the reader cannot
 * tell it apart from a bug.
 *
 * `rollup` and `absent` share one string on purpose. The remedy is the same -
 * select a single entity - and the difference between "you are on All
 * Entities" and "nothing is selected" is not something the reader can act on
 * differently. Only `archived` needs its own copy, because switching entity is
 * NOT the remedy there: the current selection is read-only, so the sentence
 * has to say so.
 */
export function entityScopeActionHintKey(reason: EntityScopeBlockReason): string {
  return reason === "archived"
    ? "entityScopeRequired.actionNeedsActiveEntity"
    : "entityScopeRequired.actionNeedsEntity";
}

/** The URL parameter an entity-owned link (e.g. a scheduled-report email) uses to name its entity. */
export const ENTITY_DEEP_LINK_PARAM = "entityId";

/**
 * What to do with an `?entityId=` deep link (OCT-15904).
 *
 * - `none`     - the URL names no entity; leave the selection alone.
 * - `switch`   - the URL names an entity this user can pick; switch to it.
 * - `rejected` - the URL names an entity the user cannot pick (another
 *                workspace's, unknown, or archived). Keep the current
 *                selection; never widen to "All Entities".
 *
 * Only an entity already in the user's own entity list can be selected, so
 * a forged link can at most pick an entity the user could choose by hand -
 * the server still validates the `X-Octa-Entity-Id` header on every request.
 */
export type EntityDeepLink =
  | { readonly kind: "none"; }
  | { readonly kind: "switch"; readonly entityId: string; }
  | { readonly kind: "rejected"; readonly entityId: string; };

export function resolveEntityDeepLink(
  search: string,
  entities: ReadonlyArray<{ readonly id: string; readonly isArchived?: boolean | null; }>,
): EntityDeepLink {
  const entityId = new URLSearchParams(search).get(ENTITY_DEEP_LINK_PARAM)?.trim();
  if (!entityId) return { kind: "none" };
  const match = entities.find((e) => e.id === entityId);
  return match && !match.isArchived ? { kind: "switch", entityId } : { kind: "rejected", entityId };
}

/** `search` with the entity deep-link parameter removed (keeps every other parameter). */
export function stripEntityDeepLink(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(ENTITY_DEEP_LINK_PARAM);
  const rest = params.toString();
  return rest ? `?${rest}` : "";
}
